import { useAllNetworksBenchmarksQuery } from '@/queries/allNetworksBenchmarks.query'
import type { VaultApyRange } from '@/types/apy'
import type { ChartPeriod } from '@/types/periods'
import { getApyValue } from '@/utils/apy'
import { cn } from '@/utils/cn'
import { getNetworkName } from '@/utils/network/getNetworkName'
import { getValueColor } from '@/utils/styles/getValueColor'
import { SECOND_IN_MILLISECONDS } from '@vaultsfyi/common/constants'
import { formatAmount, formatDate, formatDecimalToPercentage, subDays, toUnixTimestamp } from '@vaultsfyi/common/utils'
import { useMemo } from 'react'
import type { TooltipProps } from 'recharts'
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { ChartBrush, ChartBrushOverlay } from '../charts/chartBrush'
import { ChartContainer } from '../charts/chartContainer'
import { ChartTooltip } from '../charts/chartTooltip'
import { ChartWatermark } from '../charts/chartWatermark'
import { Loader } from '../common/loader'
import { NetworkImage } from '../common/networkImage'
import { TokenImage } from '../common/tokenImage'

interface Props {
  code: 'usd' | 'eth'
  apyRange: VaultApyRange
  includeRewards: boolean
  period: ChartPeriod
  selectedNetworks: Set<string>
  colorMap: Record<string, string>
  yDomain?: [number, number]
  layout?: 'compact' | 'full'
  showBrush?: boolean
}

export const BenchmarkNetworksChart = ({
  code,
  apyRange,
  includeRewards,
  period,
  selectedNetworks,
  colorMap,
  yDomain,
  layout = 'full',
  showBrush = false,
}: Props) => {
  const { data: allBenchmarkData, isFetching } = useAllNetworksBenchmarksQuery({ code })

  const availableNetworks = useMemo(() => {
    if (!allBenchmarkData) return []
    return Object.keys(allBenchmarkData).sort()
  }, [allBenchmarkData])

  const filteredData = useMemo(() => {
    if (!allBenchmarkData) return {}
    const cutoff = Math.floor(toUnixTimestamp(subDays(Date.now(), period)))
    const result: Record<string, NonNullable<typeof allBenchmarkData>[string]> = {}
    for (const network of availableNetworks) {
      result[network] = (allBenchmarkData[network] ?? []).filter((p) => p.timestamp >= cutoff)
    }
    return result
  }, [allBenchmarkData, availableNetworks, period])

  const { currentValue, valueDiff } = useMemo(() => {
    const mainnetData = filteredData['mainnet']
    if (!mainnetData || mainnetData.length === 0) return { currentValue: 0, valueDiff: 0 }

    const lastItem = mainnetData[mainnetData.length - 1]
    const firstItem = mainnetData[0]
    const current = lastItem?.apy ? getApyValue({ apy: lastItem.apy, apyRange, includeRewards }) : 0
    const past = firstItem?.apy ? getApyValue({ apy: firstItem.apy, apyRange, includeRewards }) : 0
    return { currentValue: current, valueDiff: current - past }
  }, [filteredData, apyRange, includeRewards])

  const chartData = useMemo(() => {
    if (availableNetworks.length === 0) return []

    const timestampSet = new Set<number>()
    for (const network of availableNetworks) {
      for (const point of filteredData[network] ?? []) {
        timestampSet.add(point.timestamp)
      }
    }

    const lookups: Record<string, Map<number, number>> = {}
    for (const network of availableNetworks) {
      const map = new Map<number, number>()
      for (const point of filteredData[network] ?? []) {
        if (point.apy) {
          map.set(point.timestamp, getApyValue({ apy: point.apy, apyRange, includeRewards }))
        }
      }
      lookups[network] = map
    }

    return Array.from(timestampSet)
      .sort((a, b) => a - b)
      .map((ts) => {
        const point: Record<string, number | undefined> & { timestamp: number } = { timestamp: ts }
        for (const network of availableNetworks) {
          point[network] = lookups[network].get(ts)
        }
        return point
      })
  }, [filteredData, availableNetworks, apyRange, includeRewards])

  const chart = (
    <ChartContainer className={cn(layout === 'compact' ? 'h-36 flex-1' : showBrush ? 'h-72' : 'h-48')}>
      {isFetching && chartData.length === 0 ? (
        <Loader />
      ) : (
        <ResponsiveContainer>
          <LineChart data={chartData} margin={{ top: 4, right: 0, bottom: 0, left: 0 }}>
            <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="rgb(var(--border))" />
            <XAxis
              dataKey="timestamp"
              tickLine={false}
              tickMargin={8}
              type="number"
              domain={['dataMin', 'dataMax']}
              interval="preserveStartEnd"
              tickCount={4}
              tickFormatter={(ts) => formatDate({ timestamp: ts * SECOND_IN_MILLISECONDS })}
            />
            <YAxis
              width={52}
              tickLine={false}
              tickMargin={4}
              tickFormatter={(value) =>
                `${formatAmount(value * 100, { notation: 'compact', maximumFractionDigits: 1 })}%`
              }
              domain={yDomain ?? ['auto', 'auto']}
            />
            <Tooltip content={<NetworksTooltip />} />
            {availableNetworks.map((network) => (
              <Line
                key={network}
                dataKey={network}
                type="monotone"
                stroke={colorMap[network] ?? 'rgb(var(--body-foreground))'}
                strokeWidth={network === 'mainnet' ? 3 : 1}
                opacity={network === 'mainnet' ? 1 : 0.5}
                dot={false}
                connectNulls
                isAnimationActive={false}
                hide={!selectedNetworks.has(network)}
              />
            ))}
            <ChartWatermark />
            {showBrush && availableNetworks.length > 0 && (
              <ChartBrush data={chartData}>
                <LineChart>
                  <YAxis hide domain={['auto', 'auto']} />
                  {availableNetworks.map((network) => (
                    <Line
                      key={network}
                      dataKey={network}
                      type="monotone"
                      stroke={colorMap[network] ?? 'rgb(var(--body-foreground))'}
                      strokeWidth={network === 'mainnet' ? 2 : 1}
                      opacity={network === 'mainnet' ? 1 : 0.55}
                      dot={false}
                      connectNulls
                      isAnimationActive={false}
                      hide={!selectedNetworks.has(network)}
                    />
                  ))}
                  <ChartBrushOverlay />
                </LineChart>
              </ChartBrush>
            )}
          </LineChart>
        </ResponsiveContainer>
      )}
    </ChartContainer>
  )

  if (layout === 'compact') {
    return (
      <div className="flex flex-wrap">
        <div className="flex w-[152px] shrink-0 flex-col justify-center gap-2">
          <div className="flex flex-col gap-0.5">
            <div className="flex items-center gap-2">
              <p className="text-body-foreground text-sm">
                <span className="uppercase">{code}</span> rate (Ethereum)
              </p>
            </div>
            <p className="font-bold text-3xl text-heading-foreground">
              {isFetching && selectedNetworks.size === 0 ? (
                <>
                  <span className="animate-pulse duration-700">–</span>
                  <span className="animate-pulse delay-300 duration-700">–</span>
                </>
              ) : (
                formatDecimalToPercentage(currentValue)
              )}
            </p>
          </div>
          <p className="whitespace-nowrap text-xs">
            <span className={cn(getValueColor(valueDiff))}>
              {valueDiff > 0 && '+'}
              {formatDecimalToPercentage(valueDiff)}
            </span>{' '}
            vs prev. {period} days
          </p>
        </div>
        <div className="grow basis-60">{chart}</div>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <TokenImage symbol={code === 'eth' ? 'eth' : 'usdc'} className="size-7" />
          <p className="text-body-foreground">
            <span className="uppercase">{code}</span> rate (Ethereum)
          </p>
        </div>
        <p className="font-bold text-3xl text-heading-foreground">
          {isFetching && selectedNetworks.size === 0 ? (
            <>
              <span className="animate-pulse duration-700">–</span>
              <span className="animate-pulse delay-300 duration-700">–</span>
            </>
          ) : (
            formatDecimalToPercentage(currentValue)
          )}
        </p>
        <p className="whitespace-nowrap text-sm">
          <span className={cn(getValueColor(valueDiff))}>
            {valueDiff > 0 && '+'}
            {formatDecimalToPercentage(valueDiff)}
          </span>{' '}
          vs prev. {period} days
        </p>
      </div>
      {chart}
    </div>
  )
}

const NetworksTooltip = ({ payload, label }: TooltipProps<number, string>) => {
  if (!payload || payload.length === 0) return null

  const sorted = [...payload].filter((e) => typeof e.value === 'number').sort((a, b) => (b.value ?? 0) - (a.value ?? 0))

  return (
    <ChartTooltip>
      <p className="mb-1">{formatDate({ timestamp: Number(label) * SECOND_IN_MILLISECONDS })}</p>
      {sorted.map((entry) => (
        <div key={String(entry.dataKey)} className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-1.5">
            <div className="size-2 rounded-full" style={{ backgroundColor: entry.color }} />
            <NetworkImage network={String(entry.dataKey)} className="size-4" />
            <span className="first-letter:uppercase">{getNetworkName(String(entry.dataKey))}</span>
          </div>
          <span className="font-semibold tabular-nums">{`${((entry.value ?? 0) * 100).toFixed(2)}%`}</span>
        </div>
      ))}
    </ChartTooltip>
  )
}
